import { type ReactNode, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@utilities-one/gpm-ui';

import { systemSettingQueryOptions } from '@/entities/setting/query/setting.queries';
import { AccessContext, type AccessContextType, meQueryKey } from '@/entities/user/providers/accessContext';
import type { WithPermission, WithRestriction } from '@/entities/user/types';
import * as access from '@/entities/user/utils/access';
import { api, DetailedError, parseResponse } from '@/library/api/honoClient';

/**
 * The first `/users/me` right after the post-login full reload can transiently
 * fail 401 — the just-created Session row may not be readable yet
 * (read-after-write), so the token doesn't resolve to an active session.
 * Retry a bounded number of times before treating the user as logged-out.
 * Mirrors the UG-4367 guard in gpm-webapp's AccessProvider.
 */
const ME_RETRY_LIMIT = 2;
const ME_RETRY_DELAY_MS = 300;

const isUnauthorized = (error: unknown): boolean => error instanceof DetailedError && error.statusCode === 401;

/**
 * Cookie-JWT session gate.
 *
 * - Resolving `/users/me` → centered spinner. The system settings load in parallel and hold the
 *   spinner too, so `useHrbsManaged()` is settled for the whole app (a failed load reads as off).
 * - 401 after the retry budget → `fallback` (the public login page).
 * - Non-401 failure → minimal error panel (API down ≠ logged out; rendering
 *   the login form would be a lie — submitting couldn't succeed either).
 * - Success → provides {@link AccessContextType} and renders `children`.
 *
 * Note: `redirectToLoginOn401` (honoClient fetch wrapper) hard-redirects
 * 401s to `/` on any *other* path, so deep links land here on `/` and get
 * the login screen; on `/` itself it stays put and this provider decides.
 */
export const AccessProvider = ({ children, fallback }: { children: ReactNode; fallback: ReactNode }) => {
  const meQuery = useQuery({
    queryKey: meQueryKey,
    queryFn: () => parseResponse(api.users.me.$post()),
    retry: (failureCount) => failureCount < ME_RETRY_LIMIT,
    retryDelay: ME_RETRY_DELAY_MS,
    staleTime: 5 * 60 * 1000,
  });
  const settingQuery = useQuery({ ...systemSettingQueryOptions(), retry: 1 });

  if (meQuery.isError) {
    if (isUnauthorized(meQuery.error)) {
      return <>{fallback}</>;
    }
    return (
      <div className="bg-background text-foreground flex min-h-screen flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-lg font-semibold">Can&apos;t reach the REIPM API</p>
        <p className="text-muted-foreground text-sm">Check your connection and try again.</p>
        <button
          type="button"
          className="text-primary text-sm font-medium underline underline-offset-4"
          onClick={() => void meQuery.refetch()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (meQuery.isPending || settingQuery.isPending) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center" role="status" aria-label="Loading">
        <Spinner className="size-8" />
      </div>
    );
  }

  return (
    <AuthenticatedAccess user={meQuery.data} refresh={meQuery.refetch}>
      {children}
    </AuthenticatedAccess>
  );
};

const AuthenticatedAccess = ({
  user,
  refresh,
  children,
}: {
  user: AccessContextType['user'];
  refresh: () => Promise<unknown>;
  children: ReactNode;
}) => {
  const hasPermission = useCallback((...permissions: string[]) => access.hasPermission(user, ...permissions), [user]);

  const hasAccess = useCallback((permission?: string | string[]) => access.hasAccess(user, permission), [user]);

  const filterByPermissions = useCallback(
    <T,>(list: WithPermission<T>[]) => access.filterByPermissions(user, list),
    [user]
  );

  const filterByRestrictions = useCallback(
    <T,>(list: WithRestriction<T>[]) => access.filterByRestrictions(user, list),
    [user]
  );

  const value = useMemo<AccessContextType>(
    () => ({
      user,
      hasPermission,
      hasAccess,
      filterByPermissions,
      filterByRestrictions,
      refresh: async () => {
        await refresh();
      },
    }),
    [user, hasPermission, hasAccess, filterByPermissions, filterByRestrictions, refresh]
  );

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};
